/*
 * app-wrapper.tsx
 *
 * Diese Basis-Komponente wird in der Regel von uns benutzt,
 * - um (1) die Komponenten-Struktur vorzuschreiben und
 * - um (2) wichtige States zu definieren, die mit anderen Komponenten geteilt werden.
 *
 * In der Vergangenheit zeigte sich allerdings, dass diese Basis-Komponente sich häufig
 * schnell sehr stark aufbläht, was die Nachvollziehbarkeit erschwert.
 * Daher sollte in regelmäßigen Abständen überprüft werden, ob man HTML-Elemente nicht
 * in neue React Komponenten auslagern kann.
 */

import { useEffect } from 'react';
import shallow from 'zustand/shallow';

// Components
import { StdCloseBttn } from '../components/std-close-bttn';
import { StdSidebar } from '../components/std-sidebar';
import { StdPortraitHeader } from '../components/std-portrait-header';
import { DebugAnimationSlider } from '../components/debug/debug-animation-slider';
import { StdPlaybackPanel } from '../components/std-playback-panel';
import { StdSplashscreen } from '../components/std-splashscreen';
import { StdFloatingWindow } from '../components/std-floating-window';
import { StdVideoDialog } from '../components/std-video-dialog';
import { StdSideMenus } from './std-side-menus';
import { UserInfo } from '../components/user-info';

// IDs and classes
import { id_canvasWrapper } from '../constants/html-ids';
import { cl_appWrapper, cl_appBody } from '../constants/html-classnames';

// Functions
// import { setProgressbarValue } from '../components/progressbar';
import { transl } from '../localisation/translate';
import { getCanvasWrapperState } from './get-canvas-wrapper-state';
import { insideElectron, debugMode } from '../../common/access-point';
import { isHiddenOnTrue } from '../utils/css-helpers';
import { v3dAdapter } from '../verge3d-adapter/v3d-adapter';
import { useStore } from '../../state/ui-store';

// Import custom style always at last!
import './app-wrapper.css';
import { evt_Resize } from '../constants/event-names';

const selector = ({
  uiHidden,
  activeLanguage,
  infoBttnActive,
  helpBttnActive,
  settingsBttnActive,
  langBttnActive,
  videoUri,
  playbarBttnActive,
}: State) => ({
  uiHidden,
  activeLanguage,
  infoBttnActive,
  helpBttnActive,
  settingsBttnActive,
  langBttnActive,
  videoUri,
  playbarBttnActive,
});

export const AppWrapper = () => {
  const {
    uiHidden,
    activeLanguage,
    infoBttnActive,
    helpBttnActive,
    settingsBttnActive,
    langBttnActive,
    videoUri,
    playbarBttnActive,
  } = useStore(selector, shallow);

  // useEffect hook is called once on start, because there are no dependencies to watch
  useEffect(() => {
    v3dAdapter();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.dispatchEvent(new Event(evt_Resize));
  }, [playbarBttnActive]);

  const clMain = `${cl_appWrapper} ${isHiddenOnTrue(uiHidden)}`;

  return (
    <div className={clMain}>
      {(debugMode || insideElectron) && <StdCloseBttn />}
      <StdPortraitHeader title={transl('#app|title', activeLanguage.dict)} />
      <div className={cl_appBody}>
        <StdSidebar />
        <StdSideMenus />
        <div
          id={id_canvasWrapper}
          className={getCanvasWrapperState(
            infoBttnActive,
            helpBttnActive,
            settingsBttnActive,
            langBttnActive,
            playbarBttnActive
          )}
        >
          {/* false && <ZoomWidget
          value={zoomValue}
          onZoomChange={direction => remote.changeZoomDistance(direction)}
        /> */}

          {/* example:  window.matchMedia("(min-width:40em)").matches */}
          <StdFloatingWindow />
          <StdPlaybackPanel />
        </div>
      </div>
      {debugMode && <DebugAnimationSlider />}
      <UserInfo />
      {videoUri && <StdVideoDialog publicFilePath={videoUri} />}
      <StdSplashscreen
        imageSrc={transl('#splashscreen', activeLanguage.dict)}
        subImageSrc={transl('#tuev', activeLanguage.dict)}
      />
    </div>
  );
};
